import axios from 'axios';
import { $Q } from '../utils/query-selector';

/**
* Client for the Shopify API
*/
class API {

  /**
  * Add products to cart
  * @param {{
  *   items: object,
  *   sections: string,
  * }} config  – Contains the products to add,
  * the quantity and section to update
  * @returns {object} Line items associated with the added items and sections
  */
  async addToCart({ items, sections = undefined }) {
    const formData = {
      items: items,
    };

    //Support bundled section rendering
    if (sections) {
      formData.sections = sections;
    }

    try {
      const { data } = await axios({
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        // eslint-disable-next-line no-undef
        url: `${routes.cart_add_url}.js`,
        data: JSON.stringify(formData),
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-undef
      const cartMessage = $Q(".cart-notification");

      cartMessage.classList.toggle("hidden");

      setTimeout(() => {
        cartMessage.classList.toggle("hidden");
      }, 10000);
    }
  }

  async clearCart() {
    try {
      await axios({
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        // eslint-disable-next-line no-undef
        url: `/cart/clear.js`,
      });
    } catch (error) {
      // eslint-disable-next-line no-undef
      const cartMessage = $Q(".cart-notification");

      cartMessage.classList.toggle("hidden");

      setTimeout(() => {
        cartMessage.classList.toggle("hidden");
      }, 10000);
    }
  }

  /**
  * Update the cart's line item quantities
  * @param {{
  *   id: number,
  *   quantity: number,
  *   sections: string
  * }} config – Contains the product variant,
  * the quantity and section to update
  * @returns {object} The JSON of the cart and HTML of the sections
  */
  async updateCart({
    id,
    quantity,
    sections = undefined,
  }) {

    const formData = {
      updates: {
        [id]: quantity,
      },
    };

    //Support bundled section rendering
    if (sections) {
      formData.sections = sections;
    }

    try {
      const { data } = await axios({
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        // eslint-disable-next-line no-undef
        url: `${routes.cart_update_url}.js`,
        data: JSON.stringify(formData),
      });
      return data;
    } catch (error) {
      // eslint-disable-next-line no-undef
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * Render up to five sections with the use of Section Rendering API
  * @param {string} sections – section IDs
  * @returns {object} Includes pairs for each section ID and its
  * corresponding rendered HTML
  */
  async renderShopifySection(sections, parameters = null) {
    try {
      let url = `?sections=${sections}`;
      if (parameters) {
        url += `${parameters}`
      } 
      const {
        data: html,
      } = await axios.get(url);
      return html;
    } catch (error) {
      // eslint-disable-next-line no-undef
      console.error(`Error: ${error.message}`);
    }
  }

  async shopifySectionByUrl(base, section) {
    try {
      const {
        data: html,
      } = await axios.get(`${base}?section_id=${section}`);
      return html;
    } catch (error) {
      // eslint-disable-next-line no-undef
      console.error(`Error: ${error.message}`);
    }
  }

  /**
   * Render HTML with given variant
   * @param {String} base - URL for the API call
   * @param {String} variantID - Id of the selected variant
   * @returns {Object} - Section mainproduct for variant selected
   *
   * @author Andres Briñez
   */

   async shopifyVariantByUrl(base, variantID, section=null) {
    try {
      let urlWithSection = section ? "&sections=" + section : "";
      const {
        data: html,
      } = await axios.get(`${base}?variant=${variantID}${urlWithSection}`);
      return html;
    } catch (error) {
      // eslint-disable-next-line no-undef
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * It is used to recommend related products for a specific product
  * @param {{
  *   id: number,
  *   limit: number,
  *   sectionId: string
  * }} config – Product ID on which the recommendation will be based,
  * the result limit and the section with which the recommended
  * products will be rendered
  * @returns {object} Array with the recommended products or, if the section ID
  * is supplied, HTML from a section rendered with product recommendations
  */
  async getRecommendedProducts({ id, limit, sectionId = undefined }) {
    try {
      const locale = window.theme.lang === "fr" ? "" : window.theme.lang === "pt-PT" ? "/pt" : `/${window.theme.lang}`;
      let url;
      let productId = id;

      if (id === "") {
        productId = await this.read(`/products.json`)
          .then(response => response.products[0].id);
      }
      
      if (sectionId) {
        //Support section rendering
        url = `${locale}/recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}`;
      } else {
        url = `${locale}/recommendations/products.json?product_id=${productId}&limit=${limit}`;
      }
      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-undef
      console.error(`Error: ${error.message}`);
    }
  }

  /**
  * Get data from a resource
  * @param {string} url – The path of the resource to obtain
  * @returns {object} Data from a resource
  */
  async read(url) {
    try {
      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      // eslint-disable-next-line no-undef
      console.error(`Error: ${error.message}`);
    }
  }
}

export default new API();
